/**
 * ZIP-Code Flat Rate Pricing Types and Utilities
 *
 * A standalone sibling to `flat-rate.ts` (the polygon-based system). ZIP flat
 * rates always apply between a configured HUB (e.g. the airport — a real address
 * with a detection radius) and a ZIP zone, in EITHER direction:
 *   - hub  -> ZIP zone   (e.g. airport to a suburb)
 *   - ZIP zone -> hub    (e.g. a suburb to the airport)
 * There are no other modes. A trip qualifies when one end is at ANY configured
 * hub and the other end's ZIP falls in a zone; that zone's rule sets the price.
 *
 * The ZIP-string helpers (`normalizeZip`, `expandZipSpec`) are shared between the
 * builder import wizard (which must absorb messy operator rate cards) and the
 * runtime matcher, so normalization behaves identically everywhere.
 */

// =============================================================================
// TYPES
// =============================================================================

/**
 * A service zone defined by a set of ZIP codes.
 */
export interface ZipFlatRateZone {
  id: string
  name: string
  description?: string | null
  /** Normalized 5-digit ZIP codes in this zone */
  zipCodes: string[]
  color: string
  isActive: boolean
}

/**
 * A hub location (e.g. an airport). Radius-based: a trip end counts as the hub
 * when it is within `radiusMiles` of the hub coordinates.
 */
export interface ZipFlatRateHub {
  id: string
  name: string
  address: string
  lat: number
  lng: number
  radiusMiles: number
  isActive: boolean
}

/**
 * A ZIP-based flat rate rule: a price for trips between its ZIP zone and any hub.
 */
export interface ZipFlatRateRule {
  id: string
  name: string
  description?: string | null
  zipZone: ZipFlatRateZone
  flatRate: number
  vehicleIds: string[] | null // null = applies to all vehicles
  priority: number
  isActive: boolean
}

/**
 * One end of a trip, as seen by the matcher: its ZIP and whether it is a hub.
 */
export interface ZipTripEnd {
  zip: string | null
  isHub: boolean
}

/**
 * Result of checking for a ZIP flat rate match.
 */
export interface ZipFlatRateMatch {
  rule: ZipFlatRateRule
  flatRate: number
  matchedZone: ZipFlatRateZone
  /** true when the trip is hub -> ZIP zone (vs ZIP zone -> hub) */
  isFromHub: boolean
}

// =============================================================================
// ZIP STRING HELPERS
// =============================================================================

/**
 * Normalize a raw ZIP token to a canonical 5-digit string.
 *
 * Strips everything that is not a digit, then takes the first 5 digits. This
 * tolerates operator junk like ZIP+4 (`30301-1234` -> `30301`) and textual
 * suffixes (`30301DT` -> `30301`). Returns null if fewer than 5 digits remain.
 *
 * @param raw - Raw ZIP token
 * @returns Normalized 5-digit ZIP, or null if invalid
 */
export function normalizeZip(raw: string | null | undefined): string | null {
  if (raw == null) {
    return null
  }
  const digits = String(raw).replace(/\D/g, '')
  if (digits.length < 5) {
    return null
  }
  return digits.slice(0, 5)
}

/**
 * Expand a raw ZIP specification into a list of normalized 5-digit ZIP codes.
 *
 * Handles the messy formats operators provide on rate cards:
 *   - single:        "30002"            -> ["30002"]
 *   - slash list:    "28001/28002"      -> ["28001", "28002"]
 *   - comma/space:   "28001, 28002"     -> ["28001", "28002"]
 *   - numeric range: "27601-27617"      -> ["27601" ... "27617"] (17 codes)
 *   - junk suffix:   "30301DT"          -> ["30301"]
 *
 * Ranges are detected when a token contains a hyphen/en-dash/em-dash separating
 * two 5-digit numbers; they expand inclusively. Results are de-duplicated while
 * preserving first-seen order. Invalid tokens are skipped.
 *
 * @param raw - Raw ZIP specification from a single cell/field
 * @returns De-duplicated array of normalized 5-digit ZIP codes
 */
export function expandZipSpec(raw: string | null | undefined): string[] {
  if (raw == null) {
    return []
  }

  const seen = new Set<string>()
  const result: string[] = []

  const add = (zip: string | null) => {
    if (zip && !seen.has(zip)) {
      seen.add(zip)
      result.push(zip)
    }
  }

  // Split list separators first: slash, comma, ampersand, semicolon, whitespace.
  const tokens = String(raw)
    .split(/[/,;&]|\s+/)
    .map((t) => t.trim())
    .filter((t) => t.length > 0)

  for (const token of tokens) {
    // Detect a numeric range like 27601-27617 (hyphen / en-dash / em-dash).
    const rangeMatch = token.match(/^(\d{5})\s*[-–—]\s*(\d{5})$/)
    if (rangeMatch) {
      const start = parseInt(rangeMatch[1], 10)
      const end = parseInt(rangeMatch[2], 10)
      if (start <= end && end - start <= 10000) {
        for (let n = start; n <= end; n++) {
          add(String(n).padStart(5, '0'))
        }
        continue
      }
    }

    add(normalizeZip(token))
  }

  return result
}

/**
 * Check whether a ZIP code belongs to a zone.
 *
 * @param zip - Normalized ZIP code (or raw; will be normalized)
 * @param zone - ZIP flat rate zone
 * @returns true if the ZIP is in the zone
 */
export function isZipInZone(
  zip: string | null | undefined,
  zone: ZipFlatRateZone
): boolean {
  const normalized = normalizeZip(zip)
  if (!normalized) {
    return false
  }
  return zone.zipCodes.includes(normalized)
}

// =============================================================================
// MATCHING
// =============================================================================

/**
 * Check whether a single rule matches the given trip ends.
 *
 * A rule matches when one end is a hub and the other end's ZIP is in the rule's
 * zone (either direction).
 */
function checkRuleMatch(
  pickup: ZipTripEnd,
  dropoff: ZipTripEnd,
  vehicleId: string,
  rule: ZipFlatRateRule
): ZipFlatRateMatch | null {
  // Vehicle scope
  if (rule.vehicleIds !== null && !rule.vehicleIds.includes(vehicleId)) {
    return null
  }

  // hub -> ZIP zone: pickup is the hub, dropoff ZIP is in the zone
  if (pickup.isHub && isZipInZone(dropoff.zip, rule.zipZone)) {
    return {
      rule,
      flatRate: rule.flatRate,
      matchedZone: rule.zipZone,
      isFromHub: true,
    }
  }

  // ZIP zone -> hub: dropoff is the hub, pickup ZIP is in the zone
  if (dropoff.isHub && isZipInZone(pickup.zip, rule.zipZone)) {
    return {
      rule,
      flatRate: rule.flatRate,
      matchedZone: rule.zipZone,
      isFromHub: false,
    }
  }

  return null
}

/**
 * Find the matching ZIP flat rate rule for a trip.
 *
 * Returns the highest priority (lowest priority number) match, or null. At the
 * same priority level, vehicle-specific rules take precedence over rules that
 * apply to all vehicles. Mirrors the polygon `findFlatRateMatch` ordering.
 *
 * @param pickup - Pickup trip end ({ zip, isHub })
 * @param dropoff - Dropoff trip end ({ zip, isHub })
 * @param vehicleId - Vehicle ID to check
 * @param rules - Array of ZIP flat rate rules (should be pre-filtered to active)
 * @returns ZipFlatRateMatch if found, null otherwise
 */
export function findZipFlatRateMatch(
  pickup: ZipTripEnd,
  dropoff: ZipTripEnd,
  vehicleId: string,
  rules: ZipFlatRateRule[]
): ZipFlatRateMatch | null {
  if (!rules || rules.length === 0) {
    return null
  }

  // Exactly one end must be a hub for a hub<->zone trip to be possible.
  if (pickup.isHub === dropoff.isHub) {
    return null
  }

  const normalized: { pickup: ZipTripEnd; dropoff: ZipTripEnd } = {
    pickup: { zip: normalizeZip(pickup.zip), isHub: pickup.isHub },
    dropoff: { zip: normalizeZip(dropoff.zip), isHub: dropoff.isHub },
  }

  const sortedRules = [...rules].sort((a, b) => {
    if (a.priority !== b.priority) {
      return a.priority - b.priority
    }
    const aIsSpecific = a.vehicleIds !== null
    const bIsSpecific = b.vehicleIds !== null
    if (aIsSpecific !== bIsSpecific) {
      return aIsSpecific ? -1 : 1
    }
    return 0
  })

  for (const rule of sortedRules) {
    if (!rule.isActive) {
      continue
    }
    const match = checkRuleMatch(
      normalized.pickup,
      normalized.dropoff,
      vehicleId,
      rule
    )
    if (match) {
      return match
    }
  }

  return null
}

// =============================================================================
// TRANSFORMATION HELPERS
// =============================================================================

interface ZipZoneDbRow {
  id: string
  name: string
  description?: string | null
  zip_codes: string[] | null
  color: string
  is_active: boolean
}

/**
 * Transform a database row to a ZipFlatRateZone.
 */
export function transformZipZoneFromDb(row: ZipZoneDbRow): ZipFlatRateZone {
  return {
    id: row.id,
    name: row.name,
    description: row.description,
    zipCodes: row.zip_codes ?? [],
    color: row.color,
    isActive: row.is_active,
  }
}

/**
 * Transform a database row to a ZipFlatRateHub.
 */
export function transformZipHubFromDb(row: {
  id: string
  name: string
  address: string
  lat: number
  lng: number
  radius_miles: number
  is_active: boolean
}): ZipFlatRateHub {
  return {
    id: row.id,
    name: row.name,
    address: row.address,
    lat: row.lat,
    lng: row.lng,
    radiusMiles: Number(row.radius_miles),
    isActive: row.is_active,
  }
}

/**
 * Transform a database row with joined zone to a ZipFlatRateRule.
 */
export function transformZipRuleFromDb(row: {
  id: string
  name: string
  description?: string | null
  zip_zone: ZipZoneDbRow
  flat_rate: number
  vehicle_ids: string[] | null
  priority: number
  is_active: boolean
}): ZipFlatRateRule {
  return {
    id: row.id,
    name: row.name,
    description: row.description,
    zipZone: transformZipZoneFromDb(row.zip_zone),
    flatRate: row.flat_rate,
    vehicleIds: row.vehicle_ids,
    priority: row.priority,
    isActive: row.is_active,
  }
}
